import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';
import { 
  ChartBarIcon, 
  EnvelopeIcon, 
  KeyIcon,
  ShieldCheckIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    const result = await login(formData.email, formData.password);
    setLoading(false);
    
    if (result.success) {
      toast.success('Welcome back!');
      navigate('/');
    } else {
      setError(result.error);
    }
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };
  
  return (
    <div className="min-h-screen bg-surface-900 flex">
      {/* Left side - branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary-900 via-surface-900 to-surface-950 items-center justify-center p-12">
        <div className="max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <ChartBarIcon className="w-12 h-12 text-primary-400" />
            <div>
              <h1 className="text-3xl font-bold text-white">Neoshift BI</h1>
              <p className="text-primary-300">Admin Dashboard</p>
            </div>
          </div>
          
          <h2 className="text-2xl font-semibold text-white mb-4">
            Platform Administration
          </h2>
          <p className="text-surface-300 mb-8">
            Manage companies, users, track issues, and get AI-powered insights 
            to improve your BI platform.
          </p>
          
          <div className="bg-surface-800/50 rounded-xl p-6 border border-surface-700">
            <div className="flex items-center gap-3 mb-3">
              <ShieldCheckIcon className="w-8 h-8 text-primary-400" />
              <span className="text-surface-100 font-medium">Admin Access Only</span>
            </div>
            <p className="text-surface-400 text-sm">
              This dashboard is restricted to Super Admins and Managing Admins only.
              Regular users should use the main application.
            </p>
          </div>
        </div>
      </div>
      
      {/* Right side - form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <ChartBarIcon className="w-10 h-10 text-primary-500" />
            <div>
              <h1 className="text-2xl font-bold text-white">Neoshift BI</h1>
              <p className="text-surface-400 text-sm">Admin Dashboard</p>
            </div>
          </div>
          
          <div className="card">
            <h2 className="text-xl font-semibold text-surface-100 mb-2">
              Admin Login
            </h2>
            <p className="text-surface-400 mb-6">
              Sign in with your admin credentials
            </p>
            
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6 flex items-center gap-3">
                <ExclamationCircleIcon className="w-5 h-5 text-red-400 flex-shrink-0" />
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-500" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="input pl-10"
                    placeholder="admin@yourcompany.com"
                    autoComplete="email"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-1">
                  Password
                </label>
                <div className="relative">
                  <KeyIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-500" />
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="input pl-10"
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                </div>
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full mt-6"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>
            
            <div className="mt-6 pt-6 border-t border-surface-700">
              <p className="text-surface-500 text-sm text-center">
                Need help? Contact your system administrator.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

